import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { playerSidebar } from '@/config/sidebarConfig.ts';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { getTeamsApi } from '@/lib/api/teamsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import {
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateAbbreviatedFr,
} from '@shared';
import { CompetitionStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { Competition, Team } from '@shared/types/entities';
import { isUserPremium } from '@shared/utils/premium.utils';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Calendar, ClipboardCheck, Flag, History, Search, Trophy, User, UserCircle } from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

// ─── Helpers ─────────────────────────────────────────────
const REGISTRATION_CONFIG: Record<TeamRegistrationStatus, { label: string; color: string }> = {
  [TeamRegistrationStatus.CONFIRMED]: { label: 'Confirmée', color: 'text-emerald-600' },
  [TeamRegistrationStatus.PENDING]: { label: 'En attente', color: 'text-amber-600' },
  [TeamRegistrationStatus.REJECTED]: { label: 'Refusée', color: 'text-red-600' },
  [TeamRegistrationStatus.CANCELLED]: { label: 'Annulée', color: 'text-slate-500' },
};

function daysUntil(d: string) {
  const diff = Math.ceil((new Date(d).getTime() - Date.now()) / 86_400_000);
  if (diff < 0) return 'Passé';
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Demain';
  return `Dans ${diff} jours`;
}

// ─── Sub-components ───────────────────────────────────────
function TeamCard({ team }: { team: Team }) {
  const cfg = REGISTRATION_CONFIG[team.registrationStatus];
  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-orange-300 hover:shadow-lg'>
      <div className='mb-3 flex items-start justify-between'>
        <div>
          <span className={`text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
          <h3 className='mt-1 font-bold text-slate-900'>{team.name}</h3>
          <p className='text-xs text-slate-500'>Inscrit le {formatDateAbbreviatedFr(team.createdAt)}</p>
        </div>
      </div>
      <div className='grid grid-cols-2 gap-4 border-t border-slate-100 pt-3 text-sm'>
        <div>
          <p className='text-xs text-slate-500'>Paiement</p>
          <p className={`font-semibold ${team.paymentStatus === 'COMPLETED' ? 'text-emerald-600' : 'text-amber-600'}`}>
            {team.paymentStatus === 'COMPLETED' ? 'Payé' : 'En attente'}
            {team.paidAmount ? ` · ${(team.paidAmount / 100).toFixed(0)}€` : ''}
          </p>
        </div>
        <div>
          <p className='text-xs text-slate-500'>Statut</p>
          <p className={`font-semibold ${cfg.color}`}>{cfg.label}</p>
        </div>
      </div>
    </div>
  );
}

function CompetitionAvailableCard({ c }: { c: Competition }) {
  const remaining = c.maxTeams ? c.maxTeams - c.currentTeams : null;
  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-orange-300'>
      <div className='mb-3 flex items-start justify-between'>
        <div className='flex-1'>
          <span className='rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700'>
            Inscriptions ouvertes
          </span>
          <h3 className='mt-2 font-bold text-slate-900'>{c.name}</h3>
          <p className='text-xs text-slate-500'>{c.date ? formatDateAbbreviatedFr(c.date) : '—'}</p>
        </div>
        <span className='ml-2 shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700'>
          {c.type ? TYPE_LABELS[c.type] : '—'}
        </span>
      </div>
      <div className='flex items-center justify-between border-t border-slate-100 pt-3 text-sm'>
        <div>
          <p className='text-xs text-slate-500'>Places restantes</p>
          <p className='font-bold text-slate-900'>{remaining !== null ? `${remaining}` : '—'}</p>
        </div>
        <div className='text-right'>
          <p className='text-xs text-slate-500'>Prix</p>
          <p className='font-bold text-orange-600'>{c.price ? `${(c.price / 100).toFixed(0)}€` : 'Gratuit'}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────
export default function PlayerDashboard() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const displayName = user?.firstName ?? user?.username ?? 'Joueur';
  const clubId = user?.clubId ?? undefined;
  const FRONT_URL = import.meta.env.VITE_URL_FRONT as string;

  // Équipes du club du joueur (inscriptions récentes)
  const {
    data: teamsData,
    isLoading: teamsLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.teams.list({ clubId, limit: 4 }),
    queryFn: () => getTeamsApi({ clubId, limit: 4, sortBy: 'createdAt', sortOrder: 'desc' }),
    enabled: !!clubId && isAuthenticated,
  });

  // Concours avec inscriptions ouvertes
  const { data: compsData, isLoading: compsLoading } = useQuery({
    queryKey: queryKeys.competitions.list({ status: CompetitionStatus.REGISTRATION_OPEN, limit: 4 }),
    queryFn: () => getCompetitionsApi({ status: CompetitionStatus.REGISTRATION_OPEN, limit: 4 }),
    enabled: isAuthenticated,
  });

  const teams = teamsData?.data ?? [];
  const competitions = compsData?.data ?? [];

  // Stat cards depuis les données disponibles
  const confirmedTeams = teams.filter(t => t.registrationStatus === TeamRegistrationStatus.CONFIRMED).length;
  const pendingTeams = teams.filter(t => t.registrationStatus === TeamRegistrationStatus.PENDING).length;
  const nextComp = competitions[0];

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={playerSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={playerSidebar}>
      <div className='p-8'>
        <PageHeader
          title={`Bonjour, ${displayName}`}
          subtitle='Prêt pour votre prochain concours ?'
          actions={
            <a
              href={`${FRONT_URL}/concours`}
              target={'_blank'}
              className='flex items-center gap-2 rounded-xl bg-orange-600 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-orange-200 transition hover:bg-orange-700'
            >
              <Search className='h-5 w-5' />
              Trouver un concours
            </a>
          }
        />

        {/* Stats */}
        <div className='mb-8 grid grid-cols-2 gap-4 md:grid-cols-4'>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100'>
              <ClipboardCheck className='h-5 w-5 text-orange-600' />
            </div>
            <p className='text-2xl font-extrabold text-slate-900'>{teamsLoading ? '…' : teams.length}</p>
            <p className='mt-1 text-sm text-slate-500'>
              {confirmedTeams} confirmée{confirmedTeams > 1 ? 's' : ''}
              {pendingTeams > 0 ? ` · ${pendingTeams} en attente` : ''}
            </p>
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100'>
              <Calendar className='h-5 w-5 text-blue-600' />
            </div>
            <p className='text-2xl font-extrabold text-slate-900'>{compsLoading ? '…' : competitions.length}</p>
            <p className='mt-1 text-sm text-slate-500'>Concours ouverts</p>
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100'>
              <Trophy className='h-5 w-5 text-emerald-600' />
            </div>
            <p className='mt-1 text-sm font-semibold text-slate-900'>
              {nextComp?.date ? daysUntil(nextComp.date) : '—'}
            </p>
            <p className='mt-1 text-xs text-slate-500'>{nextComp ? nextComp.name : 'Prochain concours'}</p>
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100'>
              <Flag className='h-5 w-5 text-purple-600' />
            </div>
            <p className='text-sm font-bold text-slate-900'>{user?.club?.name ?? '—'}</p>
            <p className='mt-1 text-xs text-slate-500'>{user?.club?.city ?? 'Mon club'}</p>
          </div>
        </div>

        {/* Main */}
        <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
          {/* Inscriptions + concours disponibles */}
          <div className='space-y-8 lg:col-span-2'>
            {/* Mes équipes récentes */}
            {clubId && (
              <div>
                <div className='mb-4 flex items-center justify-between'>
                  <h2 className='text-xl font-bold text-slate-900'>Inscriptions récentes</h2>
                  <Link
                    to={ROUTES.PLAYER_INSCRIPTIONS}
                    className='flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700'
                  >
                    Voir tout <ArrowRight className='h-4 w-4' />
                  </Link>
                </div>
                {teamsLoading ? (
                  <div className='space-y-4'>
                    {Array.from({ length: 2 }).map((_, i) => (
                      <div key={i} className='h-28 animate-pulse rounded-2xl bg-slate-200' />
                    ))}
                  </div>
                ) : teams.length === 0 ? (
                  <div className='rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500'>
                    Aucune inscription pour le moment
                  </div>
                ) : (
                  <div className='space-y-4'>
                    {teams.map(t => (
                      <TeamCard key={t.id} team={t} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Concours disponibles */}
            <div>
              <div className='mb-4 flex items-center justify-between'>
                <h2 className='text-xl font-bold text-slate-900'>Concours disponibles</h2>
                <a
                  href={`${FRONT_URL}/concours`}
                  className='flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700'
                  target={'_blank'}
                >
                  Voir tout <ArrowRight className='h-4 w-4' />
                </a>
              </div>
              {compsLoading ? (
                <div className='grid gap-4 md:grid-cols-2'>
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className='h-32 animate-pulse rounded-2xl bg-slate-200' />
                  ))}
                </div>
              ) : competitions.length === 0 ? (
                <div className='rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500'>
                  Aucun concours avec inscriptions ouvertes
                </div>
              ) : (
                <div className='grid gap-4 md:grid-cols-2'>
                  {competitions.map(c => (
                    <CompetitionAvailableCard key={c.id} c={c} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className='space-y-6'>
            {/* Actions rapides */}
            <div className='rounded-2xl border border-slate-200 bg-white p-6'>
              <h3 className='mb-4 font-bold text-slate-900'>Actions rapides</h3>
              <div className='space-y-2'>
                <a
                  href={`${FRONT_URL}/concours`}
                  className='flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50'
                  target={'_blank'}
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100'>
                    <Search className='h-5 w-5 text-orange-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Rechercher un concours</p>
                    <p className='text-xs text-slate-500'>{competitions.length} disponibles</p>
                  </div>
                </a>
                <Link
                  to={ROUTES.PLAYER_PROFILE}
                  className='flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100'>
                    <User className='h-5 w-5 text-blue-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Modifier mon profil</p>
                    <p className='text-xs text-slate-500'>Infos personnelles</p>
                  </div>
                </Link>
                <a
                  href={`${FRONT_URL}/profil/${user?.id}`}
                  className='flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100'>
                    <UserCircle className='h-5 w-5 text-indigo-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Mon profil public</p>
                    <p className='text-xs text-slate-500'>Afficher votre page public</p>
                  </div>
                </a>
                <Link
                  to={ROUTES.PLAYER_HISTORY}
                  className='flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100'>
                    <History className='h-5 w-5 text-emerald-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Mon historique</p>
                    <p className='text-xs text-slate-500'>Concours passés</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Mon profil */}
            <div className='rounded-2xl border border-slate-200 bg-white p-6'>
              <h3 className='mb-4 font-bold text-slate-900'>Mon profil</h3>
              <div className='space-y-3 text-sm'>
                {user?.licenseNumber && (
                  <div className='flex items-center justify-between'>
                    <span className='text-slate-500'>Licence</span>
                    <span className='font-semibold text-emerald-600'>{user.licenseNumber}</span>
                  </div>
                )}
                <div className='flex items-center justify-between'>
                  <span className='text-slate-500'>Email</span>
                  <span className='font-semibold text-slate-900 truncate ml-2'>{user?.email}</span>
                </div>
                {user?.mobile && (
                  <div className='flex items-center justify-between'>
                    <span className='text-slate-500'>Mobile</span>
                    <span className='font-semibold text-slate-900'>{user.mobile}</span>
                  </div>
                )}
                <div className='flex items-center justify-between'>
                  <span className='text-slate-500'>Plan</span>
                  <span
                    className={`font-semibold ${isUserPremium(user?.userPlan) ? 'text-emerald-600' : 'text-slate-500'}`}
                  >
                    {isUserPremium(user?.userPlan) ? 'Plus' : 'Free'}
                  </span>
                </div>
              </div>
            </div>

            {/* Mon club */}
            {user?.club && (
              <div className='rounded-2xl bg-gradient-to-br from-orange-600 to-red-600 p-6 text-white'>
                <h3 className='mb-4 flex items-center gap-2 font-bold'>
                  <Flag className='h-5 w-5' />
                  Mon club
                </h3>
                <div className='rounded-xl bg-white/10 p-4 backdrop-blur'>
                  <div className='flex items-center gap-3'>
                    <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 text-lg font-bold'>
                      {(user.club.name ?? '—').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className='font-bold'>{user.club.name}</p>
                      <p className='text-sm text-orange-100'>{user.club.city}</p>
                    </div>
                  </div>
                </div>
                <Link
                  to={ROUTES.CLUB_DASHBOARD}
                  className='mt-4 block w-full rounded-xl bg-white/20 px-4 py-2 text-center text-sm font-semibold transition hover:bg-white/30'
                >
                  Voir le profil du club
                </Link>
              </div>
            )}

            {/* Prochains concours */}
            {competitions.length > 0 && (
              <div className='rounded-2xl border border-slate-200 bg-white p-6'>
                <h3 className='mb-4 flex items-center gap-2 font-bold text-slate-900'>
                  <Calendar className='h-5 w-5 text-orange-600' />
                  Prochains concours
                </h3>
                <div className='space-y-3'>
                  {competitions.slice(0, 3).map(c => (
                    <div key={c.id} className='flex items-start gap-3'>
                      <div className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100'>
                        <Trophy className='h-4 w-4 text-orange-600' />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='truncate text-sm font-semibold text-slate-900'>{c.name}</p>
                        <p className='text-xs text-slate-500'>
                          {c.date ? formatDateAbbreviatedFr(c.date) : '—'} · {c.level ? LEVEL_LABELS[c.level] : '—'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
